import { useMemo, useEffect } from "react";
import { ThreeDots as Loader } from "react-loader-spinner";
import colors from "../../styles/colors";
import {
  Container,
  ValorInvalido,
  ParcelamentoTableHead,
  ParcelamentoTableBody,
  ParcelamentoEspecialToggle,
} from "./style";
import DefaultParcelamento from "./DefaultParcelamento";
import FixedParcelamento from "./FixedParcelamento";
import { useParcelamento } from "../../hooks/queries/useParcelamento";
import { numberRemoveFormat } from "../../utils/numberFormat";

interface ParcelamentoProps {
  total: string;
  totalMedicamentos?: string;
  totalPerfumaria?: string;
  isCampanhaSolar: boolean;
  disabled?: boolean;
  onParcelamentoChange?: (data: {
    codigoParcelamento?: string | undefined;
    codigoParcelamentoEspecial?: string | undefined;
    prazo?: string | undefined;
    qtdDias?: string | undefined;
  }) => void;
}

const Parcelamento = ({
  total,
  totalMedicamentos = "R$ 0,00",
  totalPerfumaria = "R$ 0,00",
  disabled = false,
  onParcelamentoChange,
}: ParcelamentoProps) => {
  // Calcula os totais numéricos
  const totals = useMemo(() => {
    const totalLiquido = numberRemoveFormat(total);
    const medicamentos = numberRemoveFormat(totalMedicamentos);
    const perfumaria = numberRemoveFormat(totalPerfumaria);

    return {
      totalLiquido,
      totalMedicamentos: medicamentos,
      totalPerfumaria: perfumaria,
    };
  }, [total, totalMedicamentos, totalPerfumaria]);

  const parcelamento = useParcelamento(totals);

  // Notifica o componente pai quando os dados de parcelamento mudam
  useEffect(() => {
    if (onParcelamentoChange) {
      onParcelamentoChange({
        codigoParcelamento: parcelamento.codigoParcelamento,
        codigoParcelamentoEspecial: parcelamento.codigoParcelamentoEspecial,
        prazo: parcelamento.prazo,
        qtdDias: parcelamento.qtdDias,
      });
    }
  }, [
    parcelamento.codigoParcelamento,
    parcelamento.codigoParcelamentoEspecial,
    parcelamento.prazo,
    parcelamento.qtdDias,
    onParcelamentoChange,
  ]);

  const changeParcelamentoEspecial = (value: string) => {
    if (value === "") {
      parcelamento.setParcelamentoEspecial("", "");
    } else if (value.toString() === "30" || value.toString() === "60") {
      parcelamento.setParcelamentoEspecial(value, "");
    } else {
      parcelamento.setParcelamentoEspecial("", value);
    }
  };

  const changeParcelamentoType = (value: boolean) => {
    parcelamento.resetParcelamentoConditions();
    parcelamento.setUseEspecialConditions(value);
  };

  if (parcelamento.minimum === 0) {
    return null;
  }

  const totalNumber = totals.totalLiquido;

  return (
    <div
      style={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
        position: "relative",
      }}
    >
      {parcelamento.isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.7)",
            zIndex: 10,
          }}
        >
          <Loader color={colors.loader.primary} height={30} width={30} />
        </div>
      )}
      <Container>
        {totalNumber < parcelamento.minimum ? (
          <ValorInvalido>
            Para poder parcelar este pedido o valor total do pedido precisa ser
            maior ou igual a R$ {parcelamento.minimum},00.
          </ValorInvalido>
        ) : (
          <Container>
            {parcelamento.condicoesParcelamentoEspecial.length > 0 && (
              <>
                <ParcelamentoEspecialToggle>
                  <label htmlFor="useEspecial">
                    Usar condição especial
                    <input
                      type="checkbox"
                      id="useEspecial"
                      name="useEspecial"
                      checked={parcelamento.useEspecialConditions}
                      onChange={() =>
                        changeParcelamentoType(
                          !parcelamento.useEspecialConditions
                        )
                      }
                      disabled={disabled}
                    />
                  </label>
                </ParcelamentoEspecialToggle>
                <FixedParcelamento
                  qtdDias={parcelamento.qtdDias}
                  changeParcelamento={changeParcelamentoEspecial}
                  condicoes={parcelamento.condicoesParcelamentoEspecial}
                  codigoParcelamentoEspecial={
                    parcelamento.codigoParcelamentoEspecial
                  }
                  useEspecialConditions={parcelamento.useEspecialConditions}
                  disabled={disabled}
                />
              </>
            )}
            <DefaultParcelamento
              useEspecialConditions={parcelamento.useEspecialConditions}
              parcelamento={parcelamento}
              disabled={disabled}
            />
            <table>
              <ParcelamentoTableHead>
                <tr>
                  <th>Parcela</th>
                  <th>Valor</th>
                  <th>Vencimento</th>
                </tr>
              </ParcelamentoTableHead>
              <ParcelamentoTableBody>
                {Array.isArray(parcelamento.condicoesParcelamento) &&
                parcelamento.condicoesParcelamento.length > 0 ? (
                  parcelamento.condicoesParcelamento.map(value => (
                    <tr key={value.parcela}>
                      <td>{value.parcela}</td>
                      <td>R$ {value.valor}</td>
                      <td>{value.vencimento}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>Selecione as condições de parcelamento</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={3}>
                    Obs.: Podem ser adicionadas despesas administrativas
                  </td>
                </tr>
              </ParcelamentoTableBody>
            </table>
          </Container>
        )}
      </Container>
    </div>
  );
};

export default Parcelamento;
